import React, { ChangeEvent, Dispatch, SetStateAction, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { Pencil, Search, Trash, Users } from "lucide-react";

import { useDeleteCohort, useGetCohorts } from "../../hooks/cohorts-cax";
import { useGetSubClients } from "../../hooks/sub_clients";

import { TextInput } from "../shared/Inputs";
import { Tooltip } from "../shared/Tooltip";
import { CustomSelect2, ICustomSelect2Option } from "../shared/Selects";
import { EntitlementTableCells, EntitlementTableHeaderCells } from "../sub/EntitlementTableCells";

import { openModal } from "../../store/modal.slice";

import { ICohortsListItem } from "../../types/services.interfaces/cohorts-cax";
import { EtySubClient } from "../../types/entities.interface";

export const Cohorts = () => {
    const dispatch = useDispatch();

    // Filter only by search input
    const [filteredCohorts, setFilteredCohorts] = useState<ICohortsListItem[]>([]);
    const [subClientFilterValue, setSubClientFilterValue] = useState<string>('all');

    const { data: subClients } = useGetSubClients(true);
    const { data: cohorts, isLoading } = useGetCohorts(subClientFilterValue);

    const showInitialLoading = isLoading && cohorts === undefined;

    return (
        <div className='cohorts__page'>
            <p className='text-4xl text-semibold'>Cohorts</p>
            <Filters
                cohorts={cohorts}
                setFilteredCohorts={setFilteredCohorts}
                subClients={subClients}
                setSubClientFilterValueState={setSubClientFilterValue}
            />
            {showInitialLoading && (
                <div className="cohorts__data__loading">
                    <div className='list-loader' />
                    <p className="text-medium">Loading Cohorts...</p>
                </div>
            )}
            {!showInitialLoading && (
                <div className="cohorts__data">
                    {!cohorts?.length && (
                        <div className="cohorts__data__empty">
                            <p className="text-medium">No cohorts yet!</p>
                        </div>
                    )}
                    {!!cohorts?.length && (
                        <Fragment>
                            {!filteredCohorts.length && (
                                <div className="cohorts__data__empty">
                                    <p className="text-medium">No cohorts found.</p>
                                </div>
                            )}
                            {!!filteredCohorts.length && (
                                <div className="cohorts__data__table_wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Name</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-left">Sub Client</p></th>
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Members</p></th>
                                                <EntitlementTableHeaderCells />
                                                <th><p className="text-xs text-medium color--txt-disabled text-center">Action</p></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCohorts.map(cohort => (
                                                <CohortRow key={cohort.id} cohort={cohort} />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Fragment>
                    )}
                    <div className="cohorts__data__actions">
                        <button
                            className='btn btn--sm btn--radius-sm btn--primary'
                            onClick={() => dispatch(openModal({ name: 'MANAGE_COHORT_MODAL', data: { cohortId: null } }))}
                        >
                            Add Cohort
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

interface IFiltersProps {
    cohorts: ICohortsListItem[] | undefined;
    setFilteredCohorts: Dispatch<SetStateAction<ICohortsListItem[]>>;
    subClients: EtySubClient[] | undefined;
    setSubClientFilterValueState: Dispatch<SetStateAction<string>>;
};
const Filters = (props: IFiltersProps) => {
    const { cohorts, setFilteredCohorts, subClients, setSubClientFilterValueState } = props;

    const hasInitializedAssociationFilter = useRef(false);

    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [includeClientCohorts, setIncludeClientCohorts] = useState<boolean>(true);
    const [selectedSubClientIds, setSelectedSubClientIds] = useState<string[]>([]);

    useEffect(() => {
        if (!cohorts) return;
        setFilteredCohorts(cohorts.filter(cohort => {
            return cohort.name?.toLowerCase().includes(filterInputValue.toLowerCase());
        }));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohorts, filterInputValue]);

    const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(e.target.value);
    };

    useEffect(() => {
        if (!subClients || hasInitializedAssociationFilter.current) return;

        hasInitializedAssociationFilter.current = true;
        setIncludeClientCohorts(true);
        setSelectedSubClientIds(subClients.map(subClient => subClient.id.toString()));
    }, [subClients]);

    const allSubClientIds = useMemo(
        () => (subClients ?? []).map(subClient => subClient.id.toString()),
        [subClients],
    );

    const allSubClientsSelected = !allSubClientIds.length
        || allSubClientIds.every(id => selectedSubClientIds.includes(id));

    const isAllAssociations = includeClientCohorts && allSubClientsSelected;

    const isDefaultFilter = !includeClientCohorts && !selectedSubClientIds.length;

    const subClientFilterValue = useMemo(() => {
        if (isAllAssociations || isDefaultFilter) {
            return 'all';
        };

        if (includeClientCohorts && !selectedSubClientIds.length) {
            return 'client';
        };

        if (!includeClientCohorts && allSubClientsSelected) {
            return 'all-sub-clients';
        };

        if (includeClientCohorts) {
            return `client,${selectedSubClientIds.join(',')}`;
        };

        return selectedSubClientIds.join(',');
    }, [isAllAssociations, isDefaultFilter, includeClientCohorts, allSubClientsSelected, selectedSubClientIds]);

    useEffect(() => {
        setSubClientFilterValueState(subClientFilterValue);
    }, [subClientFilterValue, setSubClientFilterValueState]);

    const applyAllAssociations = () => {
        setIncludeClientCohorts(true);
        setSelectedSubClientIds(allSubClientIds);
    };

    const subClientSelectOptions = useMemo<ICustomSelect2Option[]>(() => {
        return [
            {
                value: 'all',
                label: 'All Associations',
                section: 'primary',
                checked: isAllAssociations || isDefaultFilter,
            },
            {
                value: 'client',
                label: "Only Client's",
                section: 'primary',
                checked: includeClientCohorts,
            },
            {
                value: 'all-sub-clients',
                label: 'All Sub Clients',
                section: 'primary',
                checked: allSubClientsSelected,
            },
            ...((subClients ?? []).map(subClient => ({
                value: subClient.id.toString(),
                label: subClient.name,
                section: 'default' as const,
                checked: selectedSubClientIds.includes(subClient.id.toString()),
            }))),
        ];
    }, [isAllAssociations, isDefaultFilter, includeClientCohorts, allSubClientsSelected, selectedSubClientIds, subClients]);

    const subClientSelectLabel = useMemo(() => {
        if (isAllAssociations || isDefaultFilter) {
            return 'All Associations';
        };

        if (includeClientCohorts && !selectedSubClientIds.length) {
            return "Only Client's";
        };

        if (!includeClientCohorts && allSubClientsSelected) {
            return 'All Sub Clients';
        };

        if (includeClientCohorts && selectedSubClientIds.length === 1) {
            const subClientName = subClients?.find(subClient => subClient.id.toString() === selectedSubClientIds[0])?.name;
            return subClientName ? `Only Client's + ${subClientName}` : "Only Client's";
        };

        if (includeClientCohorts) {
            return `Only Client's + ${selectedSubClientIds.length} Sub Clients`;
        };

        if (selectedSubClientIds.length === 1) {
            return subClients?.find(subClient => subClient.id.toString() === selectedSubClientIds[0])?.name ?? 'All Associations';
        };

        return `${selectedSubClientIds.length} Sub Clients`;
    }, [isAllAssociations, isDefaultFilter, includeClientCohorts, allSubClientsSelected, selectedSubClientIds, subClients]);


    const handleAssociationSelect = (option: ICustomSelect2Option) => {
        if (option.value === 'all') {
            if (isAllAssociations) return;

            applyAllAssociations();
            return;
        };

        if (option.value === 'client') {
            setIncludeClientCohorts(!includeClientCohorts);
            return;
        };

        if (option.value === 'all-sub-clients') {
            setSelectedSubClientIds(allSubClientsSelected ? [] : allSubClientIds);
            return;
        };

        const selectedValue = option.value ?? '';
        const isSelected = selectedSubClientIds.includes(selectedValue);
        const nextIds = isSelected
            ? selectedSubClientIds.filter(id => id !== selectedValue)
            : [...selectedSubClientIds, selectedValue];

        setSelectedSubClientIds(nextIds);
    };

    return (
        <div className="cohorts__filters">
            <TextInput
                icon={{ icon: <Search />, position: 'prefix' }}
                placeholder="Search"
                value={filterInputValue}
                onChange={handleFilter}
                disabled={!cohorts?.length}
            />
            <CustomSelect2
                selectedLabel={subClientSelectLabel}
                options={subClientSelectOptions}
                onSelect={handleAssociationSelect}
            />
        </div>
    );
};

const CohortRow = ({ cohort }: { cohort: ICohortsListItem }) => {
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { mutate: deleteCohort } = useDeleteCohort();

    const handleDelete = () => {
        if (isProcessing) return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL', data: {
                title: 'Delete Cohort?',
                description: `Are you sure you want to delete the cohort "${cohort.name}"? All associated members and their entitlements will be removed, but user accounts will remain.`,
                onConfirm: () => {
                    deleteCohort({ id: cohort.id, setIsProcessing: setIsProcessing });
                },
            },
        }));
    };

    return (
        <tr>
            <td><p className="text-sm text-medium">{cohort.name}</p></td>
            <td><p className="text-sm text-medium text-center">{cohort.sub_client_name ?? '-'}</p></td>
            <td><p className="text-sm color--txt-muted text-center">{cohort.member_count}</p></td>
            <EntitlementTableCells entitlements={cohort.entitlements} />
            <td>
                <div className='cohorts__data__table__actions_cell'>
                    <Tooltip content="Manage cohort & entitlements">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={() => dispatch(openModal({ name: 'MANAGE_COHORT_MODAL', data: { cohortId: cohort.id } }))}
                            disabled={isProcessing}
                        >
                            <Pencil />
                        </button>
                    </Tooltip>
                    <Tooltip content="Manage cohort members">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={() => dispatch(openModal({ name: 'MANAGE_COHORT_MEMBERS_MODAL', data: { cohortId: cohort.id } }))}
                        >
                            <Users />
                        </button>
                    </Tooltip>
                    <Tooltip content="Delete cohort">
                        <button
                            className="btn btn--icon-sm btn--radius-sm btn--secondary"
                            onClick={handleDelete}
                            disabled={isProcessing}
                        >
                            <Trash />
                        </button>
                    </Tooltip>
                </div>
            </td>
        </tr>
    );
};
